import { Injectable } from '@angular/core';
import { POL_PREVIEW_DIAGNOSTICS_HEARTBEAT_INTERVAL_MS } from 'src/app/user-identification/models/life-check';

/**
 * Acumula telemetría de preview degradado en POL y la vuelca en flush puntuales
 * (heartbeat, recovery, KO) para reducir POSTs HTTP sin perder trazabilidad.
 */
@Injectable({
	providedIn: 'root',
})
export class PolPreviewDiagnosticsBufferService {
	private zeroDimFrameCount = 0;
	private invalidFrameCount = 0;
	private invalidFrameReasons: Record<string, number> = {};
	private recoveryAttempts = 0;
	private recoverySuccesses = 0;
	private recoveryFailures = 0;
	private cameraSwitches = 0;
	private watchdogTriggers = 0;
	private degradedSinceMs: number | null = null;
	private lastVideoSnapshot: Record<string, unknown> | null = null;
	private lastHeartbeatMs = 0;
	private sessionActive = false;

	public startSession(): void {
		this.reset();
		this.sessionActive = true;
	}

	public endSession(): void {
		this.sessionActive = false;
		this.reset();
	}

	public isSessionActive(): boolean {
		return this.sessionActive;
	}

	public isDegraded(): boolean {
		return this.degradedSinceMs !== null;
	}

	public reset(): void {
		this.zeroDimFrameCount = 0;
		this.invalidFrameCount = 0;
		this.invalidFrameReasons = {};
		this.recoveryAttempts = 0;
		this.recoverySuccesses = 0;
		this.recoveryFailures = 0;
		this.cameraSwitches = 0;
		this.watchdogTriggers = 0;
		this.degradedSinceMs = null;
		this.lastVideoSnapshot = null;
		this.lastHeartbeatMs = 0;
	}

	public recordZeroDimFrame(): void {
		this.zeroDimFrameCount++;
		this.markDegraded();
	}

	public recordInvalidFrame(reason: string): void {
		this.invalidFrameCount++;
		this.invalidFrameReasons[reason] = (this.invalidFrameReasons[reason] ?? 0) + 1;
		this.markDegraded();
	}

	public recordRecoveryAttempt(success: boolean): void {
		this.recoveryAttempts++;
		if (success) {
			this.recoverySuccesses++;
		} else {
			this.recoveryFailures++;
		}
	}

	public recordCameraSwitch(): void {
		this.cameraSwitches++;
	}

	public recordWatchdogTrigger(): void {
		this.watchdogTriggers++;
	}

	public markPreviewRecovered(): void {
		this.degradedSinceMs = null;
	}

	public updateVideoSnapshot(snapshot: Record<string, unknown>): void {
		this.lastVideoSnapshot = snapshot;
	}

	public shouldSendHeartbeat(): boolean {
		if (!this.sessionActive || this.degradedSinceMs === null) {
			return false;
		}
		return Date.now() - this.lastHeartbeatMs >= POL_PREVIEW_DIAGNOSTICS_HEARTBEAT_INTERVAL_MS;
	}

	public markHeartbeatSent(): void {
		this.lastHeartbeatMs = Date.now();
	}

	public buildSummary(extra?: Record<string, unknown>): Record<string, unknown> {
		return {
			previewDegradedSummary: {
				...extra,
				zeroDimFrameCount: this.zeroDimFrameCount,
				invalidFrameCount: this.invalidFrameCount,
				invalidFrameReasons: { ...this.invalidFrameReasons },
				recoveryAttempts: this.recoveryAttempts,
				recoverySuccesses: this.recoverySuccesses,
				recoveryFailures: this.recoveryFailures,
				cameraSwitches: this.cameraSwitches,
				watchdogTriggers: this.watchdogTriggers,
				degradedSinceMs: this.degradedSinceMs,
				degradedDurationMs: this.degradedSinceMs ? Date.now() - this.degradedSinceMs : 0,
				lastVideoState: this.lastVideoSnapshot,
				flushedAt: new Date().toISOString(),
			},
		};
	}

	/** Tras un flush, conservar totales de recovery/cámara pero resetear contadores de frames. */
	public flushFrameCounters(): void {
		this.zeroDimFrameCount = 0;
		this.invalidFrameCount = 0;
		this.invalidFrameReasons = {};
	}

	private markDegraded(): void {
		if (this.degradedSinceMs === null) {
			this.degradedSinceMs = Date.now();
		}
	}
}
